import { AppDate } from '../envTypes';

import { FromTemplate, ModelBase, RequireRedaction } from './base';
import { Profile } from './profile';
import {
  PopStoreExtendedSubscriptionLevel,
  PopStoreSubscriptionLevel,
} from './creator-subscription';
import { TippingAmount } from './store';
import type { ContentModerationInfo } from './moderation';

export enum PostContentType {
  RichText = 'RICH_TEXT',
  MediaGallery = 'MEDIA_GALLERY',
}

export enum PostMediaType {
  Image = 'IMAGE',
  Video = 'VIDEO',
  LiveReplay = 'LIVE_REPLAY',
}

export enum PostMediaOriginType {
  UserUploaded = 'USER_UPLOADED',
  MediaGalleryObjectRef = 'MEDIA_GALLERY_OBJECT_REF',
  LiveReplayRef = 'LIVE_REPLAY_REF',
}

export interface PostMediaDimensions {
  width: number;
  height: number;
}

export interface PostMediaMetadata {
  dimensions?: PostMediaDimensions;
  // the blurhash of the image that can be used to render the placeholder
  // either when loading the image or when the image is not accessible
  // to the user.
  blurhash?: string;
}

export interface PostMedia {
  type: PostMediaType;
  origin?: PostMediaOriginType;
  // e.g. image/jpeg, video/mp4, application/vnd.apple.mpegurl
  contentType: string;
  // media gallery object id or live replay id
  id: string;
  url: string;
  processedUrl?: string; // processed url.
  thumbnailUrl?: string; // thumbnail image url
  thumbnailVideoUrl?: string;
  caption?: string;
  metadata?: PostMediaMetadata;
  // moderation information
  moderation?: ContentModerationInfo;
}

export enum PostStatus {
  Draft = 'DRAFT',
  Published = 'PUBLISHED',
  Scheduled = 'SCHEDULED',
}

export enum PostVisibility {
  Public = 'PUBLIC',
  Restricted = 'RESTRICTED',
  Private = 'PRIVATE',
}

// open questions:
//  1. do we allow delete or hide post is user paid for it?

// `/stores/{creatorId}/posts`
export interface Post extends ModelBase, FromTemplate {
  // unique internal post identifier
  cid: string;

  title: string; // FTS index
  description?: string; // FTS index
  tags?: string[];
  // pattern: `{urlCode}/post/{full year}/{full month}/{title_slub}{optional suffix}`
  // example: `andy.us1/post/2025/04/illum-aut-qui-ut-suscipit-non-quia`
  // example: `andy.us1/post/2025/04/illum-aut-qui-ut-suscipit-non-quia_qwert`
  permaLink?: string;

  pinned?: boolean; // pinned to the top of the post list
  pinnedAt?: AppDate; // pin timestamp

  // post status
  status: PostStatus;

  // the scheduled time to release/publish this post at
  scheduledReleaseTime?: AppDate;

  visibility: PostVisibility;

  // if set, this post will be turned to "pay to view" only mode
  // after this many days since the post publish time
  payToViewOnlyMode?: {
    enabled?: boolean;
    daysAfterPublished?: number;
  };

  subscribeToView?: {
    enabled?: boolean;
    level: PopStoreExtendedSubscriptionLevel;
  };

  payToView?: {
    enabled?: boolean;
    // support multi-currency?
    price: number;
    discountEnabled?: boolean;
    discountPrice?: number;
    discountStartDate?: AppDate;
    discountEndDate?: AppDate;
    // the hidden backing listing
    listingId?: string;
  };

  tipToView?: {
    enabled?: boolean;
  };
  tippingAmounts?: Array<TippingAmount>;

  crowdTippingToView?: {
    enabled?: boolean;
    targetAmount: number;
    currentAmount: number;
    currency: 'USD';
  };

  dm?: {
    enabled?: boolean;
    // post will only be sent as DM in DM only mode
    dmOnly: boolean;
    // specify recipients identified by given subscription levels
    recipients: PopStoreExtendedSubscriptionLevel[];
    // optional text message to be sent along with the post message
    message?: string;
    /** the timestamp the message is sent to the specified recipients */
    sentAt?: AppDate;
  };

  // primary post type
  postType: PostContentType;

  // rich text content, must exist if postType is RICH_TEXT
  textContent?: string;

  // media gallery content, must exist if postType is MEDIA_GALLERY
  medias?: PostMedia[];
  unrestrictedLeadingMediaCount?: number;

  // statistics
  commentCount: number; // top level comments count
  likeCount: number;

  // permissions
  allowComments: boolean;

  // published time (in addition to cerate time)
  publishedAt?: AppDate;

  // moderation information
  moderation?: ContentModerationInfo;

  // text content moderation info
  textContentModeration?: ContentModerationInfo;

  // whether emails have been sent to the followers of the creator
  emailSent?: boolean;
}

// post comments & replies:
//   `/stores/{creatorId}/posts/{postId}/comments`
export interface PostComment extends ModelBase {
  //e.g. 1/2/3, only support 3 levels of comments
  level: number;
  parentId?: string;
  text: string;
  userId: string;
  profile: Profile & RequireRedaction;
  // if this user is a follower of the post creator
  isFollower: boolean;
  // if the user is a subscriber, this will be the subscription level
  subscriptionLevel?: PopStoreSubscriptionLevel;
  // comment stats
  likeCount: number;
  // number of replies to this comment
  replyCount: number;
  removed: boolean;
}

// post like details:
//  `/stores/{creatorId}/posts/{postId}/likes`
export interface PostLike extends ModelBase {
  userId: string;
  profile: Profile;
}

// comment like details:
//  `/stores/{creatorId}/posts/{postId}/comments/{commentId}/likes`
export interface PostCommentLike extends ModelBase {
  userId: string;
  profile: Profile;
}
